import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { SettingStore } from '@easy-lib/state/setting/setting.store';

@Injectable({
	providedIn: 'root'
})
export class NotificationService {
	private subject = new Subject<AppNotificationData>();
	public notify$ = this.subject.asObservable();
	private notifyOrderFormSubject = new BehaviorSubject<boolean>(false);
	notifyOrderForm$ = this.notifyOrderFormSubject.asObservable();
	private isActive = true;
	constructor(private settingsStore: SettingStore) {
		window.addEventListener('focus', () => {
			this.isActive = true;
		});
		window.addEventListener('blur', () => {
			this.isActive = false;
		});
	}
	/**
	 *
	 * @param  extra should be a url path or a function.
	 */
	alert(text: string, type: NotificationType, showCount = false, extra?: string | (() => void), detailText?: string) {
		switch (type) {
			case NotificationType.danger:
				this.danger(text, showCount, extra);
				break;
			case NotificationType.info:
				this.info(text, showCount, extra);
				break;
			case NotificationType.success:
				this.success(text, showCount, extra);
				break;
			case NotificationType.warning:
				this.warning(text, showCount, extra, detailText);
				break;
			case NotificationType.secondary:
				this.secondary(text, showCount, extra);
				break;
			case NotificationType.pinkRose:
				this.pinkRose(text, showCount, extra);
				break;
			case NotificationType.globalSecondary:
				this.globalSecondary(text, showCount, extra);
				break;
			default:
				break;
		}
	}
	alertError(text: string) {
		this.danger(text);
	}
	alertSuccess(text: string) {
		this.success(text);
	}

	notif(notifyInput: NotificationInput): void {
		if (this.settingsStore.setting().showNotifications != null && !this.settingsStore.setting().showNotifications)
			return;
		if (this.isActive) return;

		if (this.checkPermission()) {
			let onActionClick = {};
			const actions = [];
			if (notifyInput.actions) {
				notifyInput.actions.map(data => {
					actions.push(this.getNotifyActions(data));
					onActionClick = Object.assign(onActionClick, this.getNotifyActionClick(data));
				});
			}
			// if (notifyInput.defaultUrl)
			onActionClick = Object.assign(onActionClick, this.getDefaultActionClick(notifyInput.defaultUrl));

			// const options: NotificationOptions = {
			// 	body: notifyInput.body ? notifyInput.body : '',
			// 	icon: '../assets/images/mofid-logo.png',
			// 	badge: '../assets/images/mofid-logo.png',
			// 	data: {
			// 		defaultUrl: notifyInput.defaultUrl,
			// 		onActionClick: onActionClick
			// 	}
			// };
		}
	}
	changeNotifyOrderFormState(state: boolean) {
		this.notifyOrderFormSubject.next(state);
	}
	getNotifyActions = (d: ActionLink) => ({ action: d.action, title: d.title });
	getNotifyActionClick = (d: ActionLink) => ({ [d.action]: { operation: 'openWindow', url: d.url } });
	getDefaultActionClick = (url?: string) => ({
		default: url ? { operation: 'navigateLastFocusedOrOpen', url: url } : { operation: 'navigateLastFocusedOrOpen' }
	});

	private danger(text: string, showCount = false, extra?: string | (() => void), detailText?: string) {
		const notificationData: AppNotificationData = {
			class: 'bg-danger-400',
			text: text,
			icon: 'mdi-close-octagon-outline',
			showCount: showCount,
			extra: extra,
			detailText
		};
		this.subject.next(notificationData);
	}

	private success(text: string, showCount = false, extra?: string | (() => void), detailText?: string) {
		const notificationData: AppNotificationData = {
			class: 'bg-success-400',
			text: text,
			icon: 'mdi-check-circle-outline',
			showCount: showCount,
			extra: extra,
			detailText
		};
		this.subject.next(notificationData);
	}

	private secondary(text: string, showCount = false, extra?: string | (() => void), detailText?: string) {
		const notificationData: AppNotificationData = {
			class: 'bg-secondary',
			text: text,
			icon: 'mdi-check',
			showCount: showCount,
			extra: extra,
			detailText
		};
		this.subject.next(notificationData);
	}

	private info(text: string, showCount = false, extra?: string | (() => void), detailText?: string) {
		const notificationData: AppNotificationData = {
			class: 'bg-global-400',
			text: text,
			icon: 'mdi-information-outline',
			showCount: showCount,
			extra: extra,
			detailText
		};
		this.subject.next(notificationData);
	}

	private pinkRose(text: string, showCount = false, extra?: string | (() => void), detailText?: string) {
		const notificationData: AppNotificationData = {
			class: 'bg-pink_rose',
			text,
			icon: 'mdi-information-outline',
			showCount,
			extra,
			detailText
		};
		this.subject.next(notificationData);
	}
	private globalSecondary(text: string, showCount = false, extra?: string | (() => void), detailText?: string) {
		const notificationData: AppNotificationData = {
			class: 'bg-global-700 text-body',
			text,
			icon: 'mdi-information-outline',
			showCount,
			extra,
			detailText
		};
		this.subject.next(notificationData);
	}
	private warning(text: string, showCount = false, extra?: string | (() => void), detailText?: string) {
		const notificationData: AppNotificationData = {
			class: 'bg-warning-400',
			text,
			icon: 'mdi-alert-outline',
			showCount,
			extra,
			detailText
		};
		this.subject.next(notificationData);
	}

	private checkPermission(): boolean {
		if (!('Notification' in window)) {
			return false;
		} else if (Notification['permission'] === 'granted') {
			return true;
		} else if (Notification['permission'] !== 'denied') {
			Notification.requestPermission().then(function (permission) {
				if (permission === 'granted') {
					return true;
				} else {
					return false;
				}
			});
		}
		return false;
	}
}

export interface AppNotificationData {
	showCount: boolean;
	text: string;
	class: string;
	icon: string;
	extra?: string | (() => void); //It can be a 'url' string or a function,
	detailText?: string;
}

export enum NotificationType {
	info,
	success,
	warning,
	danger,
	secondary,
	globalSecondary,
	pinkRose
}

export enum GeneratorBase {
	FIELD_TYPE_STRING = 0,
	FIELD_TYPE_BOOL = 1,
	FIELD_TYPE_ARRAY = 2,
	FIELD_TYPE_BUTTONS = 3,
	FIELD_TYPE_TIME_OFFSET = 4,
	SEPARATOR_FIELD = ';;',
	SEPARATOR_VALUE = '=',
	SEPARATOR_MULTI_VALUE = '$$'
}
export interface NotificationInput {
	// constructor(title?: string, body?: string, defaultUrl?: string, actions?: ActionLink[]) {

	// }
	title: string;
	body?: string;
	defaultUrl?: string;
	actions?: ActionLink[];
}
export type ActionLink = {
	action: string;
	title: string;
	url: string;
};
